import { formatValidQueryParamUrlCommaSeparated } from "../utils/common";
import { createPaginatedEndpoint } from "../utils/services.util";
import { PaginatedResponse } from "./common";
import { emptySplitApi } from "./empty-split-api";
import {
  FaxDocumentUrls,
  FaxFilters,
  FaxResponse,
  FaxesQueryParams,
  SendFaxInput
} from "./types/fax.type";

type SendFaxResponse = any;

const FAX_BASE_FIELDS =
  "id,direction,subject,sender_name,recipient_name,recipient,created_at,document,attachments{id,name,content_type,order,file},status,clinic{id,name,fax_phone},assigned_tags,patient,message,deactivated_by{id,first_name,last_name,name},deactivated_at,blocked_at";

// The home dashboard's "Faxes sent" tab reads a narrower row than the queue does. It lives here
// rather than in the component so every fax field list sits together — a surface keeping its own
// list off to the side is how it got left behind when failure detail was added.
const FAX_SENT_SUMMARY_FIELDS =
  "id,subject,recipient,recipient_name,created_at,document,status,direction," +
  "message,clinic{id,name,fax_phone},patient{id,first_name,last_name}";

const FAX_FAILURE_FIELDS =
  "failure_code,failure_category,failure_reason,failure_guidance,send_attempt_count,last_attempted_at";

// Gated separately from the failure fields: auto-retry ships after failure detail, and asking an
// older backend for either set fails the whole request (RestQL FieldNotFound).
const FAX_AUTO_RETRY_FIELDS = "auto_retry_count,next_retry_at";

export const FAXES_QUERY = `{${FAX_BASE_FIELDS}}`;

/**
 * Append the failure-detail fields to a RestQL field list, but only when the feature is on.
 *
 * RestQL raises FieldNotFound for an unknown key and fails the whole request, so a frontend that
 * asked for these unconditionally would break every fax list against a backend that predates them.
 * The flag is what keeps the two deploys independent.
 *
 * Every surface that renders `FaxStatusCell` builds its query through this, so none of them can be
 * left behind asking for a narrower field set than the cell needs — which is exactly how the home
 * dashboard ended up showing a bare "Failed" next to the queue's explained one.
 */
export const withFaxFailureFields = (
  baseFields: string,
  withFailureDetail: boolean,
  withAutoRetry: boolean = false
): string => {
  let fields = baseFields;
  if (withFailureDetail) {
    fields += `,${FAX_FAILURE_FIELDS}`;
  }
  if (withAutoRetry) {
    fields += `,${FAX_AUTO_RETRY_FIELDS}`;
  }
  return `{${fields}}`;
};

/**
 * The Fax Center queue and the patient Faxes tab. `withReadState` adds `read`, which only the
 * Fax Center's Unassigned queue shows (ENG-2511); it is gated by that flag for the same RestQL
 * FieldNotFound reason as the failure fields.
 */
export const getFaxesQuery = (
  withFailureDetail: boolean,
  withAutoRetry: boolean = false,
  withReadState: boolean = false
): string =>
  withFaxFailureFields(
    withReadState ? `${FAX_BASE_FIELDS},read` : FAX_BASE_FIELDS,
    withFailureDetail,
    withAutoRetry
  );

/** The home dashboard's "Faxes sent" tab. Same failure fields, narrower row. */
export const getFaxesSentQuery = (
  withFailureDetail: boolean,
  withAutoRetry: boolean = false
): string =>
  withFaxFailureFields(FAX_SENT_SUMMARY_FIELDS, withFailureDetail, withAutoRetry);

const faxesApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    sendFax: builder.mutation<SendFaxResponse, SendFaxInput>({
      query: ({
        recipient,
        documents,
        clinic,
        patient,
        box_file_ids,
        include_cover_page,
        notes,
        subject,
        recipient_name,
        sender_name
      }) => {
        const formData = new FormData();
        formData.append("recipient", recipient);
        formData.append("clinic", String(clinic || ""));

        if (patient) {
          formData.append("patient", String(patient));
        }

        if (documents) {
          documents.forEach((document: File, index: number) => {
            formData.append(`documents[${index}]`, document);
          });
        }

        if (patient && box_file_ids) {
          formData.append("box_file_ids", String(box_file_ids));
        }

        if (include_cover_page) {
          formData.append("include_cover_page", "true");
        }

        if (notes) {
          formData.append("notes", notes);
        }

        if (subject) {
          formData.append("subject", subject);
        }

        if (recipient_name) {
          formData.append("recipient_name", recipient_name);
        }

        if (sender_name) {
          formData.append("sender_name", sender_name);
        }

        return {
          url: `/faxes/`,
          method: "POST",
          body: formData
        };
      },
      // "PhysicianCommunications" too: a referring physician's history matches faxes at read time
      // on `Fax.recipient == fax_phone`, so a fax sent while a physician panel is expanded belongs
      // in it immediately. The panel never unmounts across a send, so nothing else would refresh it
      // — and expand, send, check is the natural path through that page. Unlike the email side
      // (which dispatches this from the physician-specific compose dialog) there is no
      // physician-specific fax form to hang it off; the fax form is generic. Cheap regardless: the
      // tag only has subscribers while a panel is open.
      //
      // What this delivers is "the fax is recorded", not "the fax is delivered": the new row arrives
      // as Processing with nothing stored behind it yet, so it is deliberately not clickable. The
      // terminal status lands later via the Documo webhook, which invalidates nothing, so the row
      // stays Processing until the panel is collapsed and re-expanded. That is the same limitation
      // the fax portal has — no polling, no websocket (features/faxes/CLAUDE.md).
      invalidatesTags: ["Faxes", "PhysicianCommunications"]
    }),
    getFaxesPaginated: createPaginatedEndpoint<FaxFilters, FaxResponse[]>(
      builder,
      {
        queryFn: (params) => ({
          url: `/faxes/?${formatValidQueryParamUrlCommaSeparated(params)}`
        }),
        providesTags: ["Faxes"],
        transformResponseFn: (response: PaginatedResponse<FaxResponse>) =>
          response.results
      }
    ),
    // Fax document URLs are pre-signed and short-lived, so they must never be served from
    // cache — the ones embedded in a list response expire an hour after that response was
    // built, and a fax queue left open in a tab hands the browser a dead link (S3 answers the
    // preview with an `AccessDenied ... Request has expired` XML body). `keepUnusedDataFor: 0`
    // drops the entry the moment the last subscriber unmounts, so re-opening a preview always
    // mints a fresh URL. Deliberately provides no tags: nothing should be able to resurrect a
    // stale entry, and nothing needs to invalidate one.
    getFaxDocumentUrls: builder.query<FaxDocumentUrls, number>({
      query: (id) => ({ url: `/faxes/${id}/document-urls/` }),
      keepUnusedDataFor: 0
    }),
    // `patientId: null` clears the assignment (SN-19001). It has to be sent explicitly rather
    // than omitted: PATCH ignores absent keys, so "no patient" and "leave the patient alone" are
    // the same request otherwise.
    updateFax: builder.mutation<
      FaxResponse,
      {
        id: number;
        patientId: number | null;
      }
    >({
      query: ({ id, patientId }) => ({
        url: `/faxes/${id}/`,
        method: "PATCH",
        body: { patient: patientId }
      }),
      invalidatesTags: ["Faxes"]
    }),
    resendFax: builder.mutation<FaxResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/faxes/${id}/`,
        method: "PATCH",
        body: { status: 1 }
      }),
      invalidatesTags: ["Faxes"]
    }),
    // Stops a scheduled automatic retry without resending. Idempotent server-side.
    // ENG-2511: previewing a fax in the Fax Center marks it read. Invalidates only the counts (the
    // queue's "N new" and the sidebar badge); the row itself is refreshed by the caller through
    // updatePaginatedData, so a preview never refetches the whole list.
    markFaxRead: builder.mutation<
      { id: number; read: boolean },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/faxes/${id}/read/`,
        method: "POST"
      }),
      invalidatesTags: ["FaxesCount"]
    }),
    // The list endpoint with limit=0 answers with the count alone (ZeroLimitOffsetPagination).
    getFaxesCount: builder.query<number, FaxesQueryParams>({
      query: (params) => ({
        url: `/faxes/?${formatValidQueryParamUrlCommaSeparated({
          ...params,
          limit: 0
        })}`
      }),
      providesTags: ["Faxes", "FaxesCount"],
      transformResponse: (response: { count: number }) => response.count
    }),
    cancelFaxRetry: builder.mutation<
      { id: number; next_retry_at: null },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/faxes/${id}/cancel-retry/`,
        method: "POST"
      }),
      invalidatesTags: ["Faxes"]
    }),
    deactivateFax: builder.mutation<FaxResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/faxes/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["Faxes"]
    }),
    restoreFax: builder.mutation<FaxResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/faxes/${id}/restore/`,
        method: "POST"
      }),
      invalidatesTags: ["Faxes"]
    }),
    downloadFax: builder.mutation<FaxResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/faxes/${id}/document/`,
        method: "PATCH"
      }),
      invalidatesTags: ["Faxes"]
    }),
    blockFaxNumber: builder.mutation<
      { id: number; number: string },
      { fax: number }
    >({
      query: ({ fax }) => ({
        url: `/blocked-fax-numbers/`,
        method: "POST",
        body: { fax }
      }),
      invalidatesTags: ["Faxes"]
    }),
    unblockFaxNumber: builder.mutation<
      { detail: string; number: string },
      { fax: number }
    >({
      query: ({ fax }) => ({
        url: `/blocked-fax-numbers/unblock/`,
        method: "POST",
        body: { fax }
      }),
      invalidatesTags: ["Faxes"]
    })
  })
});

export const {
  useSendFaxMutation,
  useGetFaxDocumentUrlsQuery,
  useLazyGetFaxDocumentUrlsQuery,
  useUpdateFaxMutation,
  useResendFaxMutation,
  useCancelFaxRetryMutation,
  useMarkFaxReadMutation,
  useGetFaxesCountQuery,
  useDeactivateFaxMutation,
  useRestoreFaxMutation,
  useDownloadFaxMutation,
  useBlockFaxNumberMutation,
  useUnblockFaxNumberMutation
} = faxesApi;
